// src/components/SendMoney.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "./Navigation";
import TransactionReceipt from "./TransactionReceipt";
import RecipientSelector from "./RecipientSelector";

// ✅ FIX: Use env-based API origin (falls back to backend port 3001)
const API_ORIGIN = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";
const API_BASE = `${API_ORIGIN}/api/v2`;

// 🆕 Transaction Limits Configuration
const LIMITS = {
  MPESA:   { max: 150000, currency: "KES", label: "M-Pesa" },
  AIRTIME: { max: 150000, currency: "KES", label: "Airtime" },
  BANK:    { max: 9999,   currency: "CAD", label: "Bank Transfer" },
};

// ── Supported corridors ──────────────────────────────────────────────────────
const SEND_CURRENCIES = [
  { value: "CAD", label: "🇨🇦 CAD — Canadian Dollar", country: "CA", enabled: true },
  { value: "USD", label: "🇺🇸 USD — US Dollar",       country: "US", enabled: false },
];

const RECEIVE_OPTIONS = [
  { value: "KES", label: "🇰🇪 KES — Kenyan Shilling",    country: "KE", methods: ["MPESA","AIRTIME","BANK"], enabled: true },
  { value: "GHS", label: "🇬🇭 GHS — Ghanaian Cedi",      country: "GH", methods: ["MOBILE_MONEY","BANK"], enabled: false },
  { value: "NGN", label: "🇳🇬 NGN — Nigerian Naira",     country: "NG", methods: ["MOBILE_MONEY","BANK"], enabled: false },
  { value: "UGX", label: "🇺🇬 UGX — Ugandan Shilling",   country: "UG", methods: ["MOBILE_MONEY","BANK"], enabled: false },
  { value: "TZS", label: "🇹🇿 TZS — Tanzanian Shilling", country: "TZ", methods: ["MOBILE_MONEY","BANK"], enabled: false },
  { value: "ETB", label: "🇪🇹 ETB — Ethiopian Birr",     country: "ET", methods: ["BANK"], enabled: false },
  { value: "RWF", label: "🇷🇼 RWF — Rwandan Franc",      country: "RW", methods: ["MOBILE_MONEY","BANK"], enabled: false },
  { value: "ZAR", label: "🇿🇦 ZAR — South African Rand", country: "ZA", methods: ["BANK"], enabled: false },
];
const FX_HINTS = {
  'CAD-KES': '55.2',  'CAD-GHS': '10.8',   'CAD-NGN': '1,230',
  'CAD-UGX': '2,870', 'CAD-TZS': '2,045',  'CAD-ETB': '44.1',
  'CAD-RWF': '1,095', 'CAD-ZAR': '13.2',
  'USD-KES': '129.5', 'USD-GHS': '15.4',   'USD-NGN': '1,610',
  'USD-UGX': '3,740', 'USD-TZS': '2,580',  'USD-ETB': '57.3',
  'USD-RWF': '1,380', 'USD-ZAR': '18.5',
};
const getFxHint = (send, receive) => {
  const key = `${send}-${receive}`;
  return FX_HINTS[key] ? `${FX_HINTS[key]} ${receive}` : '…';
};

export default function SendMoney() {
  const navigate = useNavigate();

  // Verification gates — phone first, then KYC
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const u = JSON.parse(stored);
        if (u.phone_verified === false || u.phoneVerified === false) { navigate('/verify-phone', { replace: true }); return; }
        if (u.id_verified === false || u.idVerified === false) { navigate('/kyc', { replace: true }); return; }
      } catch (e) {}
    }
  }, [navigate]);
  // Transfer basics - MOVED TO TOP
  const [sendCurrency, setSendCurrency] = useState('CAD');
  const [receiveCurrency, setReceiveCurrency] = useState('');
  const [sendAmount, setSendAmount] = useState("");
  const [receiveAmount, setReceiveAmount] = useState("");
  const [amountInputMode, setAmountInputMode] = useState("send"); // "send" or "receive"
  const [product, setProduct] = useState("SNPN");
  const [fromCurrency, setFromCurrency] = useState("CAD");
  const [toCurrency, setToCurrency] = useState("KES");
  const selectedReceiveOption = RECEIVE_OPTIONS.find(r => r.value === toCurrency) || RECEIVE_OPTIONS[0];
  const recipientCountryFromCurrency = selectedReceiveOption.country;
  const [deliveryMethod, setDeliveryMethod] = useState("MPESA");

  // Quote state
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Transaction state
  const [txError, setTxError] = useState("");
  const [txLoading, setTxLoading] = useState(false);
  const [txResult, setTxResult] = useState(null);

  // Logged-in user (sender)
  const [user, setUser] = useState(null);
  const [senderStreet, setSenderStreet] = useState("");
  const [senderCity, setSenderCity] = useState("");
  const [senderState, setSenderState] = useState("");
  const [senderPostal, setSenderPostal] = useState("");
  const [senderCountry, setSenderCountry] = useState("CA");

  // Recipient details
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankSwiftCode, setBankSwiftCode] = useState("");
  const [bankRoutingNumber, setBankRoutingNumber] = useState("");
  const [recipientStreet, setRecipientStreet] = useState("");
  const [recipientCity, setRecipientCity] = useState("");
  const [recipientState, setRecipientState] = useState("");
  const [recipientRelationship, setRecipientRelationship] = useState("family");
  const [recipientCountry, setRecipientCountry] = useState("KE");

  // Compliance details
  const [reasonForTransfer, setReasonForTransfer] = useState("");
  const [sourceOfFunds, setSourceOfFunds] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("INTERAC");
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletAmountApplied, setWalletAmountApplied] = useState(0);

  // ✅ FIX: robust token getter (your app stores different keys in different flows)
  const getToken = () =>
    localStorage.getItem("zp_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    "";

  // 🆕 Validate transaction limits
  const validateTransactionLimits = (amount, currency, method) => {
    const limit = LIMITS[method];
    if (!limit) return { valid: true };

    // If amount is in the limit currency, check directly
    if (currency === limit.currency) {
      if (parseFloat(amount) > limit.max) {
        return {
          valid: false,
          message: `${limit.label} transfers are limited to ${limit.max.toLocaleString()} ${limit.currency}`,
        };
      }
    }

    return { valid: true };
  };

  // 🆕 Validate quote against limits
  const validateQuoteLimits = (quoteData, method) => {
    const limit = LIMITS[method];
    if (!limit) return { valid: true };

    let amountToCheck, currencyToCheck;

    if (limit.currency === "KES") {
      // Check recipient amount for mobile money limits
      amountToCheck = quoteData.recipientAmount;
      currencyToCheck = quoteData.recipientCurrency;
    } else {
      // Check sender amount for bank transfer limits
      amountToCheck = quoteData.senderAmount;
      currencyToCheck = quoteData.senderCurrency;
    }

    if (
      currencyToCheck === limit.currency &&
      parseFloat(amountToCheck) > limit.max
    ) {
      return {
        valid: false,
        message: `${limit.label} transfers are limited to ${limit.max.toLocaleString()} ${
          limit.currency
        }. Your transaction would be ${parseFloat(
          amountToCheck
        ).toLocaleString()} ${currencyToCheck}.`,
      };
    }

    return { valid: true };
  };

  // Load sender info
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        setUser(u);
        if (u.country) setSenderCountry(u.country);
        // US users send in USD (locked); Canadians keep CAD default.
        if (u.country === 'US' || u.country === 'United States') {
          setSendCurrency('USD');
          setFromCurrency('USD');
        }
        if (u.city) setSenderCity(u.city);
        if (u.state) setSenderState(u.state);
        if (u.streetAddress) setSenderStreet(u.streetAddress);
        if (u.zipCode) setSenderPostal(u.zipCode);
      }
    } catch (e) {
      console.warn("Could not parse stored user:", e);
    }
  }, []);

  // Fetch wallet balance for Step 5 payment selector
  useEffect(() => {
    const token =
      localStorage.getItem("zp_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken");
    if (!token) return;
    fetch(`${API_BASE}/wallet/balance`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          const cadBal = d.balances?.CAD
            ?? (d.wallets || []).find(w => w.currency === 'CAD')?.balance;
          if (cadBal !== undefined) setWalletBalance(parseFloat(cadBal));
        }
      })
      .catch(e => console.warn('Wallet balance fetch failed', e));
  }, []);


  // Handle recipient selection
  const handleRecipientSelect = (recipient) => {
    console.log("✅ Selected recipient:", recipient);
    setSelectedRecipient(recipient);
    setRecipientName(recipient.recipientName);
    setRecipientPhone(recipient.recipientPhone);
    setRecipientCountry(recipient.recipientCountry);
    setRecipientRelationship(recipient.relationship || "family");

    if (recipient.recipientType === "bank_account") {
      setDeliveryMethod("BANK");
    } else if (recipient.recipientType === "mobile_money") {
      setDeliveryMethod("MPESA");
    }
  };

  const handleManualRecipientChange = (field, value) => {
    setSelectedRecipient(null);

    switch (field) {
      case "name":
        setRecipientName(value);
        break;
      case "phone":
        handleRecipientPhoneChange({ target: { value } });
        break;
      case "country":
        handleRecipientCountryChange({ target: { value } });
        break;
      default:
        break;
    }
  };

  const handleRecipientPhoneChange = (e) => {
    let value = e.target.value;

    if (recipientCountry === "KE") {
      value = value.replace(/[^\d]/g, "");
      value = value.replace(/^0+/, "");
      if (value.startsWith("254")) {
        value = value.slice(3);
      }
      value = value.slice(0, 9);
      setRecipientPhone(value ? `+254${value}` : "+254");
    } else {
      setRecipientPhone(value);
    }
  };

  const handleRecipientCountryChange = (e) => {
    const value = e.target.value;
    setRecipientCountry(value);

    if (value === "KE") {
      setRecipientPhone("+254");
    } else {
      setRecipientPhone("");
    }
  };

  // Handle Send Amount change
  const handleSendAmountChange = (value) => {
    setSendAmount(value);
    setReceiveAmount("");
    setAmountInputMode("send");
    setQuote(null); // Clear quote when amount changes
  };

  // Handle Receive Amount change
  const handleReceiveAmountChange = (value) => {
    setReceiveAmount(value);
    setSendAmount("");
    setAmountInputMode("receive");
    setQuote(null); // Clear quote when amount changes
  };

  // Get quote - handles both send and receive amount modes
  const handleGetQuote = async () => {
    setQuoteError("");
    setTxError("");
    setTxResult(null);
    setQuote(null);

    const token = getToken();
    if (!token) {
      setQuoteError("You are not logged in. Please log in again.");
      return;
    }

    const amountToCalculate =
      amountInputMode === "send" ? sendAmount : receiveAmount;
    const numericAmount = Number(amountToCalculate);

    if (!numericAmount || Number.isNaN(numericAmount) || numericAmount <= 0) {
      setQuoteError("Please enter a valid amount greater than 0.");
      return;
    }


    // 🆕 Pre-validate limits before calling API
    const currency = amountInputMode === "send" ? fromCurrency : toCurrency;
    const preValidation = validateTransactionLimits(
      numericAmount,
      currency,
      deliveryMethod
    );

    if (!preValidation.valid) {
      setQuoteError(preValidation.message);
      return;
    }

    try {
      setQuoteLoading(true);

      const endpoint = `${API_BASE}/transactions/calculate`;

      const body = {
        amount: numericAmount,
          corridor: { send: sendCurrency, receive: receiveCurrency },
        fromCurrency,
        toCurrency,
        transactionType: product,
        calculationMode: amountInputMode, // "send" or "receive"
        // deliveryMethod, // optional: uncomment if backend uses it
      };

      console.log("📤 Sending calculateTransaction request:", endpoint, body);

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));
      console.log("📦 Calculate raw response:", res.status, data);

      if (!res.ok) {
        const msg =
          data.message ||
          data.error ||
          data.errors?.[0]?.msg ||
          JSON.stringify(data);
        setQuoteError(`API error ${res.status}: ${msg}`);
        setQuoteLoading(false);
        return;
      }

      let q = data;
      if (data.data) {
        q = data.data;
      }

      console.log("✅ Parsed calculation object:", q);

      // 🆕 Validate quote against limits
      const limitValidation = validateQuoteLimits(q, deliveryMethod);

      if (!limitValidation.valid) {
        setQuoteError(limitValidation.message);
        setQuoteLoading(false);
        return;
      }

      setQuote(q);

      // Auto-fill the opposite amount based on quote
      if (amountInputMode === "send") {
        setReceiveAmount(q.recipientAmount || "");
      } else {
        setSendAmount(q.senderAmount || "");
      }

      setQuoteLoading(false);
    } catch (err) {
      console.error("❗ Error fetching quote:", err);
      setQuoteError("Network error while getting quote.");
      setQuoteLoading(false);
    }
  };

  const handleConfirmSend = async () => {
    setTxError("");
    setTxResult(null);

    const token = getToken();
    if (!token) {
      setTxError("You are not logged in. Please log in again.");
      return;
    }

    if (!quote) {
      setTxError("Please get a quote before confirming the transfer.");
      return;
    }


    if (
      !senderStreet ||
      !senderCity ||
      !senderState ||
      !senderPostal ||
      !senderCountry
    ) {
      setTxError(
        "Please complete your sender address in your Profile before sending money."
      );
      return;
    }

    if (deliveryMethod === "BANK" && !bankAccountNumber) {
      setTxError("Please enter the recipient's bank account number.");
      return;
    }
    if (!recipientName || !recipientPhone) {
      setTxError("Please enter recipient name and phone number.");
      return;
    }

    if (!selectedRecipient && (!recipientStreet || !recipientCity || !recipientState)) {
      setTxError("Please provide the recipient address or select a saved recipient.");
      return;
    }

    if (!recipientRelationship) {
      setTxError("Please select the relationship with the recipient.");
      return;
    }

    if (!reasonForTransfer) {
      setTxError("Please select the reason for this transfer.");
      return;
    }

    if (!sourceOfFunds) {
      setTxError("Please select the source of funds.");
      return;
    }

    // 🆕 Final limit check before submission
    const finalValidation = validateQuoteLimits(quote, deliveryMethod);
    if (!finalValidation.valid) {
      setTxError(finalValidation.message);
      return;
    }

    try {
      setTxLoading(true);

      const endpoint = `${API_BASE}/transactions/${product.toLowerCase()}`;

      const rawSourceAmount = quote?.senderAmount ?? Number(sendAmount);
      const numericAmount = Number(rawSourceAmount);

      if (!numericAmount || Number.isNaN(numericAmount) || numericAmount <= 0) {
        setTxError("Invalid amount in quote. Please request a new quote.");
        setTxLoading(false);
        return;
      }

      const destAmount = quote?.recipientAmount ?? undefined;
      const rate = quote?.exchangeRate ?? undefined;

      const deliveryMethodValue = deliveryMethod || "MPESA";

      const recipientTypeValue =
        deliveryMethod === "MPESA" || deliveryMethod === "AIRTIME"
          ? "mobile_money"
          : "bank_account";

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';

      const body = {
        product: "SNPN",
        amount: numericAmount,
          corridor: { send: sendCurrency, receive: receiveCurrency },
        fromCurrency,
        toCurrency,
        exchangeRate: rate,
        destAmount,
        recipientName,
        recipientPhone,
        ...(deliveryMethod === "BANK" ? {
          bankAccountNumber,
          bankName,
          bankSwiftCode,
          bankRoutingNumber,
        } : {}),
        recipientCountry,
        recipientType: recipientTypeValue,
        recipientRelationship: recipientRelationship,
        deliveryMethod: deliveryMethodValue,
        compliance: {
          reasonForTransfer,
          sourceOfFunds,
        },
        timezone,
        quote,
        paymentMethod,
        walletAmountApplied: paymentMethod === 'WALLET_PARTIAL' ? walletAmountApplied : undefined,
      };

      console.log("📤 Sending SNPN transaction:", endpoint, body);

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));
      console.log("📦 Transaction response:", res.status, data);

      if (!res.ok) {
        const msg =
          data.message ||
          data.error ||
          data.errors?.[0]?.msg ||
          JSON.stringify(data);
        setTxError(`API error ${res.status}: ${msg}`);
        setTxLoading(false);
        return;
      }

      setTxResult(data);
      setTxLoading(false);
      // Deduct wallet balance locally if paid from wallet
      if (paymentMethod === 'WALLET') {
        setWalletBalance(prev => prev !== null ? Math.max(0, prev - parseFloat(sendAmount || 0)) : null);
      } else if (paymentMethod === 'WALLET_PARTIAL') {
        setWalletBalance(prev => prev !== null ? Math.max(0, prev - walletAmountApplied) : null);
      }
      // Redirect to dashboard after 3 seconds so customer sees receipt briefly
      setTimeout(() => { window.location.href = '/dashboard'; }, 3000);
    } catch (err) {
      console.error("❗ Error sending transaction:", err);
      setTxError("Network error while creating transaction.");
      setTxLoading(false);
    }
  };

  const q = quote || {};
  const displaySendAmount = q.senderAmount ?? sendAmount;
  const displayReceiveAmount = q.recipientAmount ?? receiveAmount;
  const displayRate = q.exchangeRate ?? "";
  const displayFee = q.exchangeTotalFee ?? (q.totalFees && product !== "SNPL" ? q.totalFees : (q.totalFees || 0) - (q.snplServiceFee || 0)) ?? 0;

  // 🆕 Get current limit for display
  const getCurrentLimit = () => {
    const limit = LIMITS[deliveryMethod];
    if (!limit) return null;
    return `Maximum: ${limit.max.toLocaleString()} ${limit.currency} for ${
      limit.label
    }`;
  };

  return (
    <>
      <Navigation />
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold mb-2">Send Money</h1>

        {/* 1. AMOUNT & QUOTE - MOVED TO TOP */}
        <div className="space-y-4 border p-4 rounded shadow bg-blue-50 border-blue-200">
          <h2 className="font-bold text-lg mb-1 text-blue-900">
            💰 Step 1: Choose Amount
          </h2>

          {/* Amount Input Mode Toggle */}
          <div className="bg-white p-3 rounded border border-blue-300">
            <p className="text-sm text-gray-700 mb-2 font-medium">
              How would you like to enter the amount?
            </p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="amountMode"
                  checked={amountInputMode === "send"}
                  onChange={() => {
                    setAmountInputMode("send");
                    setReceiveAmount("");
                    setQuote(null);
                  }}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">I want to send ({fromCurrency})</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="amountMode"
                  checked={amountInputMode === "receive"}
                  onChange={() => {
                    setAmountInputMode("receive");
                    setSendAmount("");
                    setQuote(null);
                  }}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">
                  Recipient receives ({toCurrency})
                </span>
              </label>
            </div>

              {/* ── Currency Corridor Selector ─────────────────────────── */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Send Currency
                  </label>
                  <select
                    value={sendCurrency}
                    disabled={senderCountry === 'US' || senderCountry === 'United States'}
                    onChange={(e) => { setSendCurrency(e.target.value); setFromCurrency(e.target.value); setReceiveCurrency(''); setToCurrency(''); setQuote(null); setQuoteError(''); }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    {SEND_CURRENCIES.map((c) => (
                      <option key={c.value} value={c.value} disabled={c.enabled === false}>
                        {c.enabled === false ? `${c.label} — Coming soon` : c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receive Currency
                  </label>
                  <select
                    value={receiveCurrency}
                    onChange={(e) => { setReceiveCurrency(e.target.value); setToCurrency(e.target.value); setQuote(null); setQuoteError(''); }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select currency…</option>
                    {RECEIVE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value} disabled={!r.enabled}>
                        {r.enabled ? r.label : `${r.label} — Coming soon`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {sendCurrency && receiveCurrency && (
                <p className="mt-2 text-xs text-gray-500">
                  Rate confirmed at checkout.{' '}
                  <span className="font-medium text-blue-600">
                    1 {sendCurrency} ≈ {getFxHint(sendCurrency, receiveCurrency)}
                  </span>
                </p>
              )}
              {/* ──────────────────────────────────────────────────────── */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Send Amount */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Send Amount ({fromCurrency})
                {amountInputMode === "send" && (
                  <span className="text-blue-600 font-bold"> ← Enter here</span>
                )}
              </label>
              <input
                type="number"
                className={`border p-3 w-full rounded text-lg font-semibold ${
                  amountInputMode === "send"
                    ? "border-blue-500 bg-white shadow-sm"
                    : "bg-gray-100 text-gray-600"
                }`}
                value={sendAmount}
                onChange={(e) => handleSendAmountChange(e.target.value)}
                placeholder={amountInputMode === "send" ? "100" : "Auto-calculated"}
                disabled={amountInputMode === "receive"}
              />
              {deliveryMethod === "BANK" && (
                <p className="text-xs text-amber-600 mt-1 font-medium">
                  ⚠️ Max: {LIMITS.BANK.max.toLocaleString()} {fromCurrency} for bank transfers
                </p>
              )}
            </div>

            {/* Receive Amount */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Receive Amount ({toCurrency})
                {amountInputMode === "receive" && (
                  <span className="text-blue-600 font-bold"> ← Enter here</span>
                )}
              </label>
              <input
                type="number"
                className={`border p-3 w-full rounded text-lg font-semibold ${
                  amountInputMode === "receive"
                    ? "border-blue-500 bg-white shadow-sm"
                    : "bg-gray-100 text-gray-600"
                }`}
                value={receiveAmount}
                onChange={(e) => handleReceiveAmountChange(e.target.value)}
                placeholder={amountInputMode === "receive" ? "10000" : "Auto-calculated"}
                disabled={amountInputMode === "send"}
              />
              {(deliveryMethod === "MPESA" || deliveryMethod === "AIRTIME") && (
                <p className="text-xs text-amber-600 mt-1 font-medium">
                  ⚠️ Max: {LIMITS[deliveryMethod]?.max?.toLocaleString()} {toCurrency} for mobile money
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Product</label>
              <select
                className="border p-2 w-full rounded"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
              >
                <option value="SNPN">SNPN (Send Now Pay Now)</option>
                <option value="SNPL">SNPL (Send Now Pay Later)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Delivery Method</label>
              <select
                className="border p-2 w-full rounded"
                value={deliveryMethod}
                onChange={(e) => {
                  setDeliveryMethod(e.target.value);
                  setQuote(null); // Clear quote when delivery method changes
                }}
              >
                {selectedReceiveOption.methods.includes("MPESA") && <option value="MPESA">M-Pesa (Max 150K KES)</option>}
                {selectedReceiveOption.methods.includes("MOBILE_MONEY") && <option value="MOBILE_MONEY">Mobile Money</option>}
                {selectedReceiveOption.methods.includes("BANK") && <option value="BANK">Bank Transfer</option>}
                {selectedReceiveOption.methods.includes("AIRTIME") && <option value="AIRTIME">Airtime Top-up</option>}
                <option value="BANK">Bank Transfer (Max 9,999 CAD)</option>
                <option value="AIRTIME">Airtime (Max 150K KES)</option>
              </select>
            </div>
          </div>

          {/* 🆕 Limit Information Banner */}
          {getCurrentLimit() && (
            <div className="bg-amber-50 border border-amber-300 p-3 rounded">
              <p className="text-sm text-amber-800">
                ⚠️ <strong>Transaction Limit:</strong> {getCurrentLimit()}
              </p>
            </div>
          )}

          {/* Get Quote Button */}
          <button
            onClick={handleGetQuote}
            className="bg-blue-600 text-white p-3 w-full rounded font-bold text-lg hover:bg-blue-700"
            disabled={quoteLoading}
          >
            {quoteLoading ? "Calculating..." : "Get Quote"}
          </button>

          {quoteError && (
            <div className="bg-red-100 text-red-700 p-3 rounded border border-red-300">
              ⚠ {quoteError}
            </div>
          )}

          {/* Quote Display */}
          {quote && (
            <div className="bg-green-50 border-2 border-green-500 p-4 rounded">
              <h3 className="font-bold text-lg mb-3 text-green-900">✅ Quote Ready</h3>
              <div className="space-y-2 text-sm">
                <p className="flex justify-between">
                  <span className="font-semibold">You send:</span>
                  <span className="text-lg font-bold">
                    ${displaySendAmount} {fromCurrency}
                  </span>
                </p>
                <p className="flex justify-between">
                  <span className="font-semibold">Recipient gets:</span>
                  <span className="text-lg font-bold text-green-700">
                    {displayReceiveAmount} {toCurrency}
                  </span>
                </p>
                <p className="flex justify-between text-gray-700">
                  <span>Exchange rate:</span>
                  <span>
                    1 {fromCurrency} = {displayRate} {toCurrency}
                  </span>
                </p>
                <p className="flex justify-between text-gray-700">
                  <span>Exchange fee:</span>
                  <span>
                    ${displayFee} {fromCurrency}
                  </span>
                </p>
                {/* SNPL Payment Plan */}
                {product === "SNPL" && quote.snplServiceFee > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-300 bg-blue-50 rounded p-3">
                    <p className="font-bold text-blue-900 mb-2">📅 SNPL Deferred Payment Plan</p>
                    <p className="flex justify-between text-gray-700">
                      <span>SNPL service fee:</span>
                      <span>${(quote.snplServiceFee || 0).toFixed(2)} {fromCurrency}</span>
                    </p>
                    <p className="flex justify-between font-bold text-blue-900 text-base mt-1">
                      <span>Total you owe:</span>
                      <span>${((quote.senderAmount || 0) + (quote.snplServiceFee || 0)).toFixed(2)} {fromCurrency}</span>
                    </p>
                    <p className="flex justify-between text-gray-700 mt-1">
                      <span>Settlement period:</span>
                      <span>14 days</span>
                    </p>
                    <p className="flex justify-between text-gray-700">
                      <span>Payment due by:</span>
                      <span className="font-semibold text-red-600">
                        {new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </span>
                    </p>
                    <p className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded border border-amber-200">
                      ⚠️ Recipient gets the money now. You repay ${((quote.senderAmount || 0) + (quote.snplServiceFee || 0)).toFixed(2)} {fromCurrency} within 14 days.
                    </p>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-3 pt-3 border-t">
                ⬇️ Now continue below to select recipient and complete the transfer
              </p>
            </div>
          )}
        </div>

        {/* 2. SENDER DETAILS */}
        <div className="space-y-4 border p-4 rounded shadow">
          <h2 className="font-bold text-lg mb-1">📍 Step 2: Sender Details</h2>

          <div className="bg-gray-50 p-4 rounded border">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Logged in as:</strong>{" "}
              {user
                ? `${user.firstName || ""} ${user.lastName || ""} (${user.email})`
                : "Unknown user"}
            </p>

            {user && (user.streetAddress || user.city || user.state) ? (
              <div className="text-sm text-gray-600 mt-3 pt-3 border-t">
                <p className="font-semibold mb-1">Address on file:</p>
                {user.streetAddress && <p>{user.streetAddress}</p>}
                {(user.city || user.state || user.zipCode) && (
                  <p>{[user.city, user.state, user.zipCode].filter(Boolean).join(", ")}</p>
                )}
                {user.country && <p>{user.country}</p>}
              </div>
            ) : (
              <div className="text-sm text-amber-700 bg-amber-50 p-3 rounded border border-amber-200 mt-3">
                <p className="font-semibold">⚠️ Address Required</p>
                <p className="mt-1">Please add your address in your profile.</p>
              </div>
            )}

            <p className="text-xs text-blue-600 mt-3">
              💡 <a href="/profile" className="underline font-medium">Update address in Profile</a>
            </p>
          </div>
        </div>

        {/* 3. RECIPIENT DETAILS */}
        <div className="space-y-4 border p-4 rounded shadow">
          <h2 className="font-bold text-lg mb-1">👤 Step 3: Recipient Details</h2>

          <div>
            <label className="block text-sm font-medium mb-2">
              Select Saved Recipient (or enter manually below)
            </label>
            <RecipientSelector
              onSelect={handleRecipientSelect}
              selectedRecipient={selectedRecipient}
              country={recipientCountry}
            />
          </div>

          {selectedRecipient && (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded">
              <p className="text-sm text-blue-800">
                ✓ Sending to <strong>{selectedRecipient.recipientName}</strong>
                {selectedRecipient.nickname && (
                  <span className="italic"> "{selectedRecipient.nickname}"</span>
                )}
              </p>
              <button
                onClick={() => setSelectedRecipient(null)}
                className="text-xs text-blue-600 underline mt-1"
              >
                Clear and enter manually
              </button>
            </div>
          )}

          <div className="border-t pt-4 mt-4">
            <p className="text-sm text-gray-600 mb-3">Or enter manually:</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  className="border p-2 w-full rounded"
                  value={recipientName}
                  onChange={(e) => handleManualRecipientChange("name", e.target.value)}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="text"
                  className="border p-2 w-full rounded"
                  value={recipientPhone}
                  onChange={(e) => handleManualRecipientChange("phone", e.target.value)}
                  placeholder="+2547XXXXXXXX"
                />
              </div>

              {deliveryMethod === "BANK" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Account Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="border p-2 w-full rounded"
                      value={bankAccountNumber}
                      onChange={(e) => setBankAccountNumber(e.target.value)}
                      placeholder="e.g. 1234567890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Bank Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="border p-2 w-full rounded"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="e.g. Equity Bank"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">SWIFT / BIC Code</label>
                    <input
                      type="text"
                      className="border p-2 w-full rounded"
                      value={bankSwiftCode}
                      onChange={(e) => setBankSwiftCode(e.target.value.toUpperCase())}
                      placeholder="e.g. EQBLKENA"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Routing / Sort Code</label>
                    <input
                      type="text"
                      className="border p-2 w-full rounded"
                      value={bankRoutingNumber}
                      onChange={(e) => setBankRoutingNumber(e.target.value)}
                      placeholder="e.g. 026009593"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <select
                  className="border p-2 w-full rounded"
                  value={recipientCountry}
                  onChange={(e) => handleManualRecipientChange("country", e.target.value)}
                >
                  <option value="KE">Kenya</option>
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                </select>
              </div>

              {!selectedRecipient ? (
                <>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Street Address</label>
                    <input
                      type="text"
                      className="border p-2 w-full rounded"
                      value={recipientStreet}
                      onChange={(e) => setRecipientStreet(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <input
                      type="text"
                      className="border p-2 w-full rounded"
                      value={recipientCity}
                      onChange={(e) => setRecipientCity(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">State / County</label>
                    <input
                      type="text"
                      className="border p-2 w-full rounded"
                      value={recipientState}
                      onChange={(e) => setRecipientState(e.target.value)}
                    />
                  </div>
                </>
              ) : (
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-600 italic bg-gray-50 p-3 rounded border">
                    ℹ️ Address not required for saved recipients
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Relationship</label>
                <select
                  className="border p-2 w-full rounded"
                  value={recipientRelationship}
                  onChange={(e) => setRecipientRelationship(e.target.value)}
                >
                  <option value="family">Family</option>
                  <option value="friend">Friend</option>
                  <option value="business">Business</option>
                  <option value="charity">Charity</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* 4. COMPLIANCE */}
        <div className="space-y-4 border p-4 rounded shadow">
          <h2 className="font-bold text-lg mb-1">📋 Step 4: Compliance Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Reason for Transfer</label>
              <select
                className="border p-2 w-full rounded"
                value={reasonForTransfer}
                onChange={(e) => setReasonForTransfer(e.target.value)}
              >
                <option value="">Select reason</option>
                <option value="family_support">Family support</option>
                <option value="education">Education</option>
                <option value="medical">Medical expenses</option>
                <option value="business_payment">Business payment</option>
                <option value="investment">Investment</option>
                <option value="travel">Travel</option>
                <option value="gift">Gift</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Source of Funds</label>
              <select
                className="border p-2 w-full rounded"
                value={sourceOfFunds}
                onChange={(e) => setSourceOfFunds(e.target.value)}
              >
                <option value="">Select source</option>
                <option value="salary">Salary</option>
                <option value="savings">Savings</option>
                <option value="business_income">Business income</option>
                <option value="loan">Loan</option>
                <option value="pension">Pension</option>
                <option value="inheritance">Inheritance</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>


        {/* 5. PAYMENT METHOD — SNPN only */}
        {product === "SNPN" && quote && (
          <div className="space-y-3 border p-4 rounded shadow bg-indigo-50 border-indigo-200">
            <h2 className="font-bold text-lg mb-1 text-indigo-900">💳 Step 5: How would you like to pay?</h2>

            {/* Option 1 — Interac */}
            <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'INTERAC' ? 'border-indigo-500 bg-white' : 'border-gray-200 bg-white hover:border-indigo-300'}`}>
              <input type="radio" name="paymentMethod" value="INTERAC" checked={paymentMethod === 'INTERAC'}
                onChange={() => { setPaymentMethod('INTERAC'); setWalletAmountApplied(0); }} className="mt-1" />
              <div>
                <p className="font-semibold text-gray-800">🏦 Interac e-Transfer</p>
                <p className="text-xs text-gray-500">ZumRails pulls ${parseFloat(quote.senderAmount || 0).toFixed(2)} CAD from your bank</p>
              </div>
            </label>

            {/* Option 2 — Full Wallet */}
            <label className={`flex items-start gap-3 p-3 rounded-lg border-2 transition ${
              walletBalance !== null && walletBalance >= parseFloat(quote.senderAmount || 0)
                ? `cursor-pointer ${paymentMethod === 'WALLET' ? 'border-indigo-500 bg-white' : 'border-gray-200 bg-white hover:border-indigo-300'}`
                : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
            }`}>
              <input type="radio" name="paymentMethod" value="WALLET" checked={paymentMethod === 'WALLET'}
                disabled={walletBalance === null || walletBalance < parseFloat(quote.senderAmount || 0)}
                onChange={() => { setPaymentMethod('WALLET'); setWalletAmountApplied(0); }} className="mt-1" />
              <div>
                <p className="font-semibold text-gray-800">💰 ZoomPay Wallet Balance</p>
                <p className="text-xs text-gray-500">
                  Available: <span className="font-bold text-green-700">${walletBalance !== null ? walletBalance.toFixed(2) : '...'} CAD</span>
                  {walletBalance !== null && walletBalance < parseFloat(quote.senderAmount || 0) && (
                    <span className="text-red-500 ml-1"> — insufficient</span>
                  )}
                </p>
              </div>
            </label>

            {/* Option 3 — Wallet Partial + Interac (only shown when wallet > 0 but < sendAmount) */}
            {walletBalance !== null && walletBalance > 0 && walletBalance < parseFloat(quote.senderAmount || 0) && (
              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${paymentMethod === 'WALLET_PARTIAL' ? 'border-indigo-500 bg-white' : 'border-gray-200 bg-white hover:border-indigo-300'}`}>
                <input type="radio" name="paymentMethod" value="WALLET_PARTIAL" checked={paymentMethod === 'WALLET_PARTIAL'}
                  onChange={() => { setPaymentMethod('WALLET_PARTIAL'); setWalletAmountApplied(walletBalance); }} className="mt-1" />
                <div className="w-full">
                  <p className="font-semibold text-gray-800">💰 + 🏦 Split: Wallet + Interac e-Transfer</p>
                  <p className="text-xs text-gray-500">Use <strong>${walletBalance.toFixed(2)} CAD</strong> from wallet + <strong>${(parseFloat(quote.senderAmount || 0) - walletBalance).toFixed(2)} CAD</strong> via Interac</p>
                </div>
              </label>
            )}
          </div>
        )}

        {/* 6. CONFIRM & SEND */}
        {quote && (
          <div className="border-2 border-green-500 p-4 rounded shadow bg-green-50">
            <button
              onClick={handleConfirmSend}
              className="bg-green-600 text-white p-3 w-full rounded font-bold text-lg hover:bg-green-700"
              disabled={txLoading}
            >
              {txLoading ? "Processing..." : "✅ Confirm & Send Money"}
            </button>

            {txError && (
              <div className="mt-3 bg-red-100 text-red-700 p-3 rounded">
                ⚠ {txError}
              </div>
            )}

            {txResult && (
              <TransactionReceipt
                transaction={(() => {
                  const t = txResult.transaction || txResult.data?.transaction || txResult.data || txResult;
                  const prefix = product === 'SNPL' ? 'SNPL' : 'SNPN';
                  return {
                    ...t,
                    transactionType: product,
                    reference: t.reference || prefix + '-' + (t.id || '').replace(/-/g,'').substring(0,6).toUpperCase(),
                    dueDate: t.snplSettlement?.dueDate || t.dueDate || (product === 'SNPL' ? new Date(Date.now() + 14*24*60*60*1000).toISOString() : null),
                  };
                })()}
                onClose={() => setTxResult(null)}
              />
            )}
          </div>
        )}

        {/* Optional: quick debug banner for YC demo builds */}
        <div className="text-xs text-gray-400 pt-2">
          API: {API_BASE}
        </div>
      </div>
    </>
  );
}
